<template>
  <div class="page">
    <menu-pages-navbar class="mb-3" />
    <!-- Header -->
    <div class="subheading mt-5">
      {{ $t('menuStartHeader') }}
    </div>
    <!-- Tab Buttons -->
    <v-layout
      align-center
      class="mt-4"
    >
      <v-btn
        flat
        round
        :color="tab === 'steps' ? 'grey darken-4' : 'grey darken-1'"
        class="subheading"
        style="text-transform: none; margin-left: -16px"
        :style="{
          'text-decoration': tab === 'steps' ? 'underline' : 'none',
        }"
        @click="tab = 'steps'"
      >
        {{ $t('menuStartHowToButton') }}
      </v-btn>
      <v-btn
        flat
        round
        :color="tab === 'pricing' ? 'grey darken-4' : 'grey darken-1'"
        class="subheading"
        style="text-transform: none"
        :style="{
          'text-decoration': tab === 'pricing' ? 'underline' : 'none',
        }"
        @click="tab = 'pricing'"
      >
        {{ $t('menuStartPricingButton') }}
      </v-btn>
    </v-layout>
    <div>
      <!-- Tab 1: Steps -->
      <div
        v-if="tab == 'steps'"
        class="mt-4"
      >
        <menu-how-to-stepper
          :step-index.sync="currentStepIndex"
          editable
          class="mt-3"
        />
      </div>
      <!-- Tab 2: Pricing -->
      <div
        v-if="tab == 'pricing'"
        class="mt-4"
      >
        <v-layout
          justify-center
          :wrap="$vuetify.breakpoint.smAndDown"
          style="align-items: stretch"
        >
          <!-- Starter -->
          <v-flex
            v-for="(pricing, pricingIndex) in pricings"
            :key="pricing.name + pricingIndex"
            xs12
            class="mb-3"
            style="max-width: 640px"
          >
            <v-card
              hover
              class="full-height"
              :class="{'mx-3': $vuetify.breakpoint.mdAndUp}"
            >
              <v-card-title>
                <v-layout
                  column
                  align-center
                >
                  <div class="headline mb-4">
                    {{ pricing.name }}
                  </div>
                  <div style="line-height: 1.05">
                    <div class="font-weight-bold grey--text text--darken-3">
                      <span style="font-size: 4.66rem">{{
                        pricing.price
                      }}</span>
                      <span
                        style="font-size: 2.33rem"
                        class="ml-1"
                      >€</span>
                    </div>
                    <div
                      class="grey--text text--darken-1"
                      style="font-size: 1.33rem"
                    >
                      {{ $t('menuStartPricingPerMonth') }}
                    </div>
                  </div>
                </v-layout>
              </v-card-title>
              <v-card-text>
                <div
                  v-if="pricing.header"
                  class="mb-4"
                >
                  {{ pricing.header }}
                </div>
                <ul class="pricing--features">
                  <li
                    v-for="(feature, featureIndex) in pricing.features"
                    :key="pricingIndex + '-feature-' + featureIndex"
                  >
                    {{ feature }}
                  </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
        <div class="mt-2 subheading text-xs-center">
          <b>{{ $t('menuStartPricingPlusSetup_1') }}:</b>
          {{ $t('menuStartPricingPlusSetup_2') }}
        </div>
      </div>
      <!-- 'Interested'-Button -->
      <v-layout justify-center>
        <v-slide-y-transition>
          <v-btn
            v-if="!!menu && !menu.has_paid_feature_interest_submitted"
            round
            color="msr"
            dark
            large
            class="mx-0"
            :class="{
              'mt-4': $vuetify.breakpoint.lgAndDown,
              'mt-5': $vuetify.breakpoint.xl,
            }"
            :loading="submittingInterest"
            @click="submitInterestAndOpenDialog()"
          >
            {{ $t('menuStartSubmitInterestedButton') }}
          </v-btn>
        </v-slide-y-transition>
      </v-layout>
    </div>
    <!-- Wizard as mandatory initial dialog -->
    <menu-how-to-stepper-dialog :show.sync="showStepperAsMandatoryDialog" />
    <!-- 'Interest submitted' Dialog -->
    <generic-confirmation-dialog
      :show.sync="showInterestSubmittedDialog"
      :loading="submittingInterest"
      persistent
      close-on-confirm
    >
      <h1
        slot="title"
        class="headline"
      >
        <v-layout
          row
          align-center
          justify-center
          wrap
        >
          <span class="text-xs-center">
            {{ $t('menuStartInterestSubmittedDialogTitle') }}
          </span>
        </v-layout>
      </h1>
      <div
        slot="content"
        class="subheading grey--text text--darken-3"
      >
        {{ $t('menuStartInterestSubmittedDialogBody') }}
      </div>
    </generic-confirmation-dialog>
  </div>
</template>

<script>
import GenericConfirmationDialog from '@/components/GenericConfirmationDialog';
import MenuHowToStepper from '@/components/Menu/MenuHowToStepper';
import MenuHowToStepperDialog from '@/components/Menu/MenuHowToStepperDialog';
import MenuPagesNavbar from '@/components/Menu/MenuPagesNavbar';

const MENUS_VISITED_BEFORE_KEY = 'menus:visited';

export function hasVisitedMenusBefore() {
  return localStorage.getItem(MENUS_VISITED_BEFORE_KEY) == 'true';
}

export default {
  components: {
    GenericConfirmationDialog,
    MenuHowToStepper,
    MenuHowToStepperDialog,
    MenuPagesNavbar,
  },
  data() {
    return {
      tab: 'steps', // One of: 'steps', 'pricing'
      currentStepIndex: 1, // Note: Must start at 1, v-stepper can't handle a 0.

      showStepperAsMandatoryDialog: false,
      submittingInterest: false,
      showInterestSubmittedDialog: false,

      pricings: [
        {
          name: this.$t('menuStartPricingStarterName'),
          price: 25,
          header: '',
          features: [
            this.$t('menuStartPricingStarterFeature_01'),
            this.$t('menuStartPricingStarterFeature_02'),
            this.$t('menuStartPricingStarterFeature_03'),
            this.$t('menuStartPricingStarterFeature_04'),
            this.$t('menuStartPricingStarterFeature_05'),
            this.$t('menuStartPricingStarterFeature_06'),
            this.$t('menuStartPricingStarterFeature_07'),
          ],
        },
        {
          name: this.$t('menuStartPricingProName'),
          price: 45,
          header: this.$t('menuStartPricingProHeader'),
          features: [
            this.$t('menuStartPricingStarterFeature_01'),
            this.$t('menuStartPricingStarterFeature_02'),
            this.$t('menuStartPricingStarterFeature_03'),
          ],
        },
      ],
    };
  },
  computed: {
    menuId() {
      return this.$route.params.menuId;
    },
    menu() {
      return this.$store.state.common.menus.filter(
        (menu) => menu.id == this.menuId
      )[0];
    },
  },
  created() {
    // Demo setup may have finished creation just before, be sure to
    // fetch that demo Menu so we e.g. may submit interest.
    this.$store.dispatch('LIST_MENUS');
  },
  mounted() {
    if (!hasVisitedMenusBefore()) {
      this.showStepperAsMandatoryDialog = true;
    }
    localStorage.setItem(MENUS_VISITED_BEFORE_KEY, true);
  },
  methods: {
    submitInterestAndOpenDialog() {
      this.submitInterestForPaidMenusFeature().then(() => {
        this.showInterestSubmittedDialog = true;

        // Frontend update
        if (this.menu) {
          this.menu.has_paid_feature_interest_submitted = true;
        }

        // Reload menus to update the 'interested' flag.
        this.$store.dispatch('LIST_MENUS');
      });
    },
    submitInterestForPaidMenusFeature() {
      this.submittingInterest = true;
      return this.$api
        .submitInterestForPaidMenusFeature(this.menuId)
        .finally(() => {
          this.submittingInterest = false;
        });
    },
  },
};
</script>

<style scoped>
.pricing--features li {
  margin-bottom: 6px;
}
</style>



// WEBPACK FOOTER //
// src/pages/MenuStart.vue